import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  BookOpen,
  Cloud,
  LifeBuoy,
  Rocket,
  Brain,
  Shield,
  ExternalLink,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { menuStructure, DOCUMENTATION_URL, type MenuGroup, type MenuItem } from './nav-menu-structure';
import { useSidebarNavPermissions } from './useSidebarNavPermissions';

type FlyoutKey = 'dashboard' | 'documentation' | string;

function groupSectionIcon(title: string): LucideIcon {
  switch (title) {
    case 'Cloud':
      return Cloud;
    case 'Support':
      return LifeBuoy;
    case 'DevOps':
      return Rocket;
    case 'AI':
      return Brain;
    case 'Administration':
      return Shield;
    default:
      return Cloud;
  }
}

interface ThinNavRailProps {
  onNavigate?: () => void;
}

type OpenMenu = { key: FlyoutKey; top: number; left: number };

export function ThinNavRail({ onNavigate }: ThinNavRailProps) {
  const location = useLocation();
  const { user } = useAuth();
  const { visibleItems, pendingApprovals } = useSidebarNavPermissions(user);
  const [openMenu, setOpenMenu] = useState<OpenMenu | null>(null);
  const railRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => {
    if (path === '/dashboard' && location.pathname === '/') return true;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const openFor = (key: FlyoutKey, anchor: HTMLElement) => {
    setOpenMenu((prev) => {
      if (prev?.key === key) return null;
      const r = anchor.getBoundingClientRect();
      return { key, top: r.top + r.height / 2, left: r.right + 4 };
    });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenu(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onScroll = () => setOpenMenu(null);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, []);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (railRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpenMenu(null);
    };
    if (openMenu) {
      document.addEventListener('mousedown', onDoc);
      return () => document.removeEventListener('mousedown', onDoc);
    }
  }, [openMenu]);

  useEffect(() => {
    setOpenMenu(null);
  }, [location.pathname]);

  const dashboardItem = menuStructure[0] as MenuItem;
  const groups = menuStructure.slice(1).filter((x): x is MenuGroup => 'items' in x);

  const renderFlyoutLink = (item: MenuItem) => {
    if (!visibleItems.has(item.path)) return null;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => {
          onNavigate?.();
          setOpenMenu(null);
        }}
        className={cn(
          'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
          isActive(item.path)
            ? 'bg-blue-600 font-medium text-white dark:bg-blue-500'
            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
        )}
      >
        <span className="shrink-0">{item.icon}</span>
        <span className="min-w-0 flex-1">{item.name}</span>
        {item.path === '/approvals' && pendingApprovals > 0 && (
          <span
            className={cn(
              'inline-flex min-h-[18px] min-w-[18px] items-center justify-center px-1 text-xs font-bold',
              isActive(item.path) ? 'bg-white/20 text-white' : 'bg-red-500 text-white',
              pendingApprovals > 99 && 'text-[10px]'
            )}
            style={{ borderRadius: 9999 }}
            aria-label={`${pendingApprovals} pending approvals`}
          >
            {pendingApprovals > 99 ? '99+' : pendingApprovals}
          </span>
        )}
      </Link>
    );
  };

  const RailButton = ({
    flyoutKey,
    label,
    Icon,
    pressed,
    anyChildActive = false,
  }: {
    flyoutKey: FlyoutKey;
    label: string;
    Icon: LucideIcon;
    pressed: boolean;
    anyChildActive?: boolean;
  }) => (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-expanded={pressed}
      onClick={(e) => openFor(flyoutKey, e.currentTarget)}
      className={cn(
        'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors',
        anyChildActive
          ? 'bg-blue-600 text-white dark:bg-blue-500'
          : pressed
            ? 'bg-gray-200 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
            : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
      )}
    >
      <Icon className="h-5 w-5" aria-hidden />
    </button>
  );

  const flyoutPortal =
    openMenu &&
    createPortal(
      <div
        ref={panelRef}
        className="fixed z-[60] min-w-[220px] rounded-lg border border-gray-200 bg-white py-2 shadow-xl dark:border-gray-800 dark:bg-gray-900"
        style={{
          top: openMenu.top,
          left: openMenu.left,
          transform: 'translateY(-50%)',
        }}
      >
        <div className="flex max-h-[min(70vh,420px)] flex-col gap-0.5 overflow-y-auto px-2 scrollbar-hidden">
          {openMenu.key === 'dashboard' &&
            visibleItems.has(dashboardItem.path) &&
            renderFlyoutLink(dashboardItem)}
          {openMenu.key === 'documentation' && (
            <a
              href={DOCUMENTATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                onNavigate?.();
                setOpenMenu(null);
              }}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <BookOpen className="h-5 w-5 shrink-0" />
              <span className="flex-1">Documentation</span>
              <ExternalLink className="h-3 w-3 shrink-0 opacity-50" aria-hidden />
            </a>
          )}
          {openMenu.key.startsWith('group:') &&
            (() => {
              const title = openMenu.key.slice('group:'.length);
              const group = groups.find((g) => g.title === title);
              if (!group) return null;
              const visibleGroupItems = group.items.filter((item) => visibleItems.has(item.path));
              return (
                <>
                  <div className="px-2 pb-1 pt-0.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {group.title}
                  </div>
                  {visibleGroupItems.map((it) => renderFlyoutLink(it))}
                </>
              );
            })()}
        </div>
      </div>,
      document.body
    );

  return (
    <>
      {flyoutPortal}
      <aside
        ref={railRef}
        aria-label="Main sections"
        className="fixed inset-y-0 left-0 z-50 flex w-14 flex-col overflow-visible border-r border-gray-200 bg-white py-2 dark:border-gray-800 dark:bg-gray-900"
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-visible px-1.5">
          <div className="flex flex-col items-center gap-1 overflow-y-auto overflow-x-visible scrollbar-hidden">
            <div className="flex flex-col items-center">
              <RailButton
                flyoutKey="dashboard"
                label="Dashboard"
                Icon={LayoutDashboard}
                pressed={openMenu?.key === 'dashboard'}
                anyChildActive={isActive(dashboardItem.path)}
              />
            </div>

            {groups.map((group) => {
              const visibleGroupItems = group.items.filter((item) => visibleItems.has(item.path));
              if (visibleGroupItems.length === 0) return null;
              const SectionIcon = groupSectionIcon(group.title);
              const groupKey = `group:${group.title}`;
              const childActive = visibleGroupItems.some((it) => isActive(it.path));
              return (
                <div key={groupKey} className="flex flex-col items-center">
                  <RailButton
                    flyoutKey={groupKey}
                    label={group.title}
                    Icon={SectionIcon}
                    pressed={openMenu?.key === groupKey}
                    anyChildActive={childActive}
                  />
                </div>
              );
            })}
          </div>

          <div className="min-h-2 flex-1" aria-hidden />

          <div className="flex flex-col items-center pb-1">
            <RailButton
              flyoutKey="documentation"
              label="Documentation"
              Icon={BookOpen}
              pressed={openMenu?.key === 'documentation'}
            />
          </div>
        </div>
      </aside>
    </>
  );
}
